/**
 * One action step, from "here is what to do" to "you did it" (spec 33
 * section 5.4, reworked 2026-10-12).
 *
 * An action step has an outcome (`until`: what the server or the page must
 * show before the step is done) and, kept for a stuck visitor only, a script
 * (`act`: what "Do this step for me" does). Mark, 2026-10-12: the
 * walkthroughs are training, not a show. So a step OPENS waiting on the
 * visitor ('self'): the card says what to do, the ring sits on the control,
 * and the step finishes when the visitor has done it. The waiter for the
 * outcome is armed FIRST, before anything else can happen, and it keeps
 * waiting through the fallback, so whoever did the work, the outcome is what
 * finishes the step.
 *
 * Outcomes (`until`):
 *   { request: { method, path, status? } }  the server answered (a save)
 *   { element: '<target>' }                  something appeared (a dialog)
 *   { field: 'picked'|'chosen'|'filled'|'checked', value?, label?, off? }
 *                                            the step's own control is done
 *                                            (tours/fieldWatch.js)
 *
 * States, as `phase`:
 *   self     waiting on the visitor (where every step starts)
 *   playing  the fallback is acting on the page ("Do this step for me")
 *   waiting  the fallback is done; the outcome has not arrived yet
 *   done | refused | timeout | cancelled   final
 *
 * A trusted click or key during `playing` hands the step straight back to the
 * visitor (`takeover`), keeping whatever was already typed. Re-typing a field
 * replaces it, so the interrupted action is safe to repeat; a press either
 * happened or did not.
 *
 * No Vue and no router: the host hands in `find` (target -> element), the
 * request log, and callbacks. That is what makes this testable in jsdom.
 */
import { AutoplayStopped, run } from './autoplay';
import { waitForElement } from './waitFor';
import { waitForField } from './fieldWatch';
import { pick } from './context';

export const FINAL = Object.freeze(['done', 'refused', 'timeout', 'cancelled']);

/**
 * @param {object} options
 * @param {object} options.step a filled step: { act, until, capture }
 * @param {(target: string) => Promise<Element|null>} options.find
 * @param {object} options.requests a request log (waitFor.js createRequestLog)
 * @param {(target: string) => string} options.selector a target as a CSS selector
 * @param {number} [options.cadenceMs] typing speed; 0 under reduced motion
 * @param {number} [options.gapMs] pause between actions
 * @param {number} [options.timeoutMs] how long the fallback waits on the outcome after acting
 * @param {number} [options.selfTimeoutMs] how long a visitor doing it by hand is waited for
 * @param {number} [options.settleMs] how long a pause in typing counts as finished (fieldWatch)
 * @param {(phase: string, detail?: object) => void} [options.onPhase]
 * @param {(op: object, el: Element, index: number) => void} [options.onOp]
 * @param {() => object|null} [options.location] where the page is (the app's
 *   current route), for a `capture` that reads it ('route.params.id')
 */
export function createActionRun({
  step,
  find,
  requests,
  selector,
  location = () => null,
  cadenceMs = 0,
  gapMs = 0,
  timeoutMs = 20000,
  selfTimeoutMs = 30 * 60000,
  settleMs = undefined,
  onPhase = () => {},
  onOp = () => {},
}) {
  const life = new AbortController(); // the whole step
  let play = null; // the current autoplay, if any
  let phase = 'self';
  let nextOp = 0;
  let settled = false;
  let resolveResult;
  const result = new Promise(resolve => {
    resolveResult = resolve;
  });

  const set = (to, detail) => {
    phase = to;
    onPhase(to, detail);
  };

  const settle = (outcome, detail = {}) => {
    if (settled) return;
    settled = true;
    if (play) play.abort();
    life.abort();
    set(outcome, detail);
    resolveResult({ outcome, ...detail });
  };

  const until = step.until || {};
  let waitingTimer = null;

  /**
   * What the step's `capture` names, once its outcome is in: 'request.po'
   * reads what was SENT (the PO the visitor typed, if they typed their own),
   * 'route.params.id' where the page is now (the driver a Find step opened:
   * an element outcome has no response to read), anything else the response
   * body.
   */
  const captureOf = entry => {
    const captured = {};
    for (const [key, path] of Object.entries(step.capture || {})) {
      const text = String(path);
      let value;
      if (text.startsWith('route.')) value = pick(location(), text.slice(6));
      else if (entry && text.startsWith('request.')) value = pick(entry.body, text.slice(8));
      else if (entry) value = pick(entry.data, text);
      if (value !== undefined) captured[key] = value;
    }
    return captured;
  };

  /** Arm the outcome now, so nothing the visitor or autoplay does can beat it. */
  const outcome = (async () => {
    // A long wait: the visitor may take their time doing it by hand. The
    // fallback narrows it (below) once the script has done its part.
    const wait = { timeoutMs: selfTimeoutMs, signal: life.signal };
    if (until.request) {
      const answer = await requests.waitFor(until.request, wait);
      if (settled) return;
      if (!answer) return settle(life.signal.aborted ? 'cancelled' : 'timeout');
      if (!answer.matched) return settle('refused', { entry: answer.entry });
      const captured = captureOf(answer.entry);
      if (until.element) {
        const el = await waitForElement(selector(until.element), {
          timeoutMs: until.elementTimeoutMs || 8000,
          signal: life.signal,
        });
        if (settled) return;
        return settle('done', { entry: answer.entry, captured, element: el });
      }
      return settle('done', { entry: answer.entry, captured });
    }
    if (until.element) {
      const el = await waitForElement(selector(until.element), wait);
      if (settled) return;
      return el ? settle('done', { element: el, captured: captureOf(null) }) : settle('timeout');
    }
    if (until.field && step.target) {
      const control = await waitForField(
        () => document.querySelector(selector(step.target)),
        until,
        { label: step.field || null, settleMs, ...wait }
      );
      if (settled) return;
      return control ? settle('done', { element: control }) : settle('timeout');
    }
    // No outcome named: the script finishing is the outcome (a dialog opened,
    // a field filled). Settled by playFrom below.
    return undefined;
  })();

  async function playFrom() {
    if (settled) return;
    play = new AbortController();
    const signal = play.signal;
    set('playing');
    const ops = (step.act || []).slice(nextOp);
    const base = nextOp;
    try {
      await run(ops, {
        find,
        cadenceMs,
        gapMs,
        signal,
        onOp: (op, el, i) => {
          nextOp = base + i;
          onOp(op, el, base + i);
        },
      });
      nextOp = (step.act || []).length;
    } catch (err) {
      if (settled) return;
      if (err instanceof AutoplayStopped && err.reason === 'aborted') return; // takeover
      if (err instanceof AutoplayStopped && err.reason === 'busy') return takeover();
      // A control is missing, disabled or offers nothing to choose: the page
      // is not what the script expected. The visitor can still finish it.
      set('self', { reason: err && err.reason ? err.reason : 'error' });
      return;
    }
    if (settled) return;
    if (!until.request && !until.element && !until.field) return settle('done');
    set('waiting');
    // The script has done its part; the outcome now has `timeoutMs` to arrive.
    waitingTimer = setTimeout(() => {
      if (!settled && phase === 'waiting') settle('timeout');
    }, timeoutMs);
  }

  function takeover() {
    if (settled || phase === 'self') return;
    if (play) play.abort();
    set('self', { reason: 'takeover' });
  }

  result.finally(() => clearTimeout(waitingTimer));
  outcome.catch(() => settle('timeout'));

  return {
    result,
    get phase() {
      return phase;
    },
    /** "Do this step for me": the fallback for a visitor who is stuck. */
    play() {
      if (settled || phase === 'playing' || phase === 'waiting') return;
      playFrom();
    },
    /** Stop, Escape, or a trusted click or key on the page during the fallback. */
    takeover,
    /** The visitor left the step (Back, Skip, Leave, wandered off). */
    cancel() {
      settle('cancelled');
    },
  };
}
